/**
 * Connections. A calm technical diagnostic surface: evidence paths, verification
 * checks and remediation — not an onboarding wizard with encouragement.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiRequestError } from '../lib/api';
import { useProjection } from '../hooks/useProjection';
import { FreshnessTag, Panel, StateChip, Unknown } from '../components/Primitives';
import { EmptyState, ErrorState, SkeletonPanel } from '../components/States';
import { relativeTime } from '../lib/format';
import { AgentKeysPanel } from '../components/AgentKeysPanel';

interface Check {
  key: string;
  label: string;
  mandatory: boolean;
  status: string;
  detail: string;
  remediation: string | null;
}

export function Integrations() {
  const { projectId } = useParams();
  const portfolio = useProjection(() => api.portfolio(), []);
  const [selected, setSelected] = useState<string | null>(projectId ?? null);
  const target = selected ?? portfolio.data?.projects[0]?.id ?? null;
  const connections = useProjection(
    () =>
      target ? api.connections(target) : Promise.resolve({ connections: [], discovery: null }),
    [target],
  );
  const [checks, setChecks] = useState<Record<string, Check[]>>({});
  const [error, setError] = useState<string | null>(null);

  if (portfolio.loading) return <SkeletonPanel rows={4} />;
  if (portfolio.error) return <ErrorState error={portfolio.error} onRetry={portfolio.reload} />;
  const projects = portfolio.data!.projects;

  async function runHealthCheck(connectionId: string) {
    if (!target) return;
    setError(null);
    try {
      const response = await api.healthCheck(target, connectionId);
      setChecks((current) => ({ ...current, [connectionId]: response.checks }));
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'The health check could not be run.');
    }
  }

  return (
    <>
      <header className="topstrip">
        <div>
          <div className="label">Project connection layer</div>
          <h1 className="topstrip__title">Integrations</h1>
          <p className="small dim">
            A project is CONNECTED only after discovery, a confirmed mapping, verification and an
            initial sync. A partial integration is DEGRADED, never healthy.
          </p>
        </div>
        <div className="topstrip__meta">
          <Link className="btn btn--sm btn--primary" to="/integrations/connect">
            Connect an existing project
          </Link>
          <label className="inline inline--tight small">
            <span className="label">Project</span>
            <select
              className="btn btn--sm"
              value={target ?? ''}
              onChange={(event) => setSelected(event.target.value)}
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {error && (
        <p className="small" data-tone="risk" style={{ color: 'var(--risk)' }} role="alert">
          {error}
        </p>
      )}

      <AgentKeysPanel projects={projects.map((p) => ({ id: p.id, name: p.name }))} />

      {connections.loading ? (
        <SkeletonPanel rows={4} />
      ) : connections.error ? (
        <ErrorState error={connections.error} onRetry={connections.reload} />
      ) : connections.data!.connections.length === 0 ? (
        <EmptyState
          tone="attention"
          title="No source is connected"
          body="This project has no connection, so nothing about it can be observed. Connect a repository through discovery, mapping confirmation and verification to start."
          action={
            <Link className="btn btn--primary" to="/integrations/connect">
              Connect an existing project
            </Link>
          }
        />
      ) : (
        connections.data!.connections.map((connection) => (
          <Panel
            key={connection.id}
            title={`${connection.provider} · ${connection.externalScopeId}`}
            subtitle={connection.stateDetail}
            actions={
              <>
                <StateChip kind="connection" state={connection.state} size="lg" />
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={() => void runHealthCheck(connection.id)}
                >
                  Run health check
                </button>
              </>
            }
          >
            <div className="grid grid--3">
              <div>
                <div className="label">Repositories</div>
                <ul
                  className="small stack stack--tight"
                  style={{ margin: '6px 0 0', paddingLeft: 16 }}
                >
                  {connection.repositories.map((repo) => (
                    <li key={repo.id}>
                      <span className="mono">{repo.fullName}</span>
                      <span className="dim">
                        {' '}
                        · {repo.defaultBranch} · {repo.role}
                      </span>
                    </li>
                  ))}
                  {connection.repositories.length === 0 && <li className="dim">none active</li>}
                </ul>
              </div>
              <div>
                <div className="label">Delivery</div>
                <dl
                  className="small"
                  style={{
                    margin: '6px 0 0',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 3,
                  }}
                >
                  <dt className="dim">Last sync</dt>
                  <dd className="mono" style={{ margin: 0 }}>
                    {relativeTime(connection.lastSyncAt)}
                  </dd>
                  <dt className="dim">Last webhook</dt>
                  <dd className="mono" style={{ margin: 0 }}>
                    {relativeTime(connection.webhookLastDeliveryAt)}
                  </dd>
                  <dt className="dim">Cursor lag</dt>
                  <dd className="mono" style={{ margin: 0 }}>
                    {connection.cursorLagSeconds === null ? (
                      <Unknown />
                    ) : (
                      `${connection.cursorLagSeconds}s`
                    )}
                  </dd>
                  <dt className="dim">Mapping coverage</dt>
                  <dd className="mono" style={{ margin: 0 }}>
                    {connection.mappingCoveragePercent === null ? (
                      <Unknown />
                    ) : (
                      `${connection.mappingCoveragePercent}%`
                    )}
                  </dd>
                </dl>
              </div>
              <div>
                <div className="label">Permissions</div>
                {connection.missingScopes.length === 0 ? (
                  <p className="small">Every required scope is granted.</p>
                ) : (
                  <>
                    <p className="small" data-tone="risk" style={{ color: 'var(--risk)' }}>
                      Missing: <span className="mono">{connection.missingScopes.join(', ')}</span>
                    </p>
                    {connection.remediation && <p className="tiny dim">{connection.remediation}</p>}
                  </>
                )}
              </div>
            </div>

            {checks[connection.id] && (
              <>
                <div className="divider" />
                <div className="label">Verification checks</div>
                <div className="rows" style={{ marginTop: 6 }}>
                  {checks[connection.id]!.map((check) => (
                    <div
                      key={check.key}
                      className="row"
                      style={{ gridTemplateColumns: 'auto 1fr auto' }}
                    >
                      <span
                        className="chip"
                        data-tone={
                          check.status === 'PASS'
                            ? 'success'
                            : check.status === 'FAIL'
                              ? check.mandatory
                                ? 'critical'
                                : 'risk'
                              : 'attention'
                        }
                      >
                        {check.status}
                      </span>
                      <div className="row__stack row__cell" data-label="Check">
                        <strong>{check.label}</strong>
                        <span className="row__sub">{check.detail}</span>
                        {check.remediation && <span className="row__sub">{check.remediation}</span>}
                      </div>
                      <span className="tiny dim row__cell" data-label="Required">
                        {check.mandatory ? 'mandatory' : 'optional'}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="divider" />
            <div className="spread">
              <FreshnessTag freshness={connection.freshness} prefix="last signal" />
              <span className="tiny dim">
                Unlinking stops ingestion. Projects, events, builds, deployments and audit history
                are retained.
              </span>
            </div>
          </Panel>
        ))
      )}
    </>
  );
}
